import { useAuth } from '../context/AuthContext';
import { useLoading } from '../context/LoadingContext';
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../api';
import { Plus, Search, Edit2, Trash2, X, TrendingDown, FileDown } from 'lucide-react';
import * as XLSX from 'xlsx';

const CATEGORIAS = ['Combustível', 'Alimentação', 'Manutenção', 'Material', 'Pedágio', 'Outros'];
const FORMAS_PAG = ['Pix', 'Cartão', 'Dinheiro', 'Mista', 'Depósito', 'Cortesia', 'A Pagar', 'A Receber'];

const CAT_CLS = {
  'Combustível':  'bg-orange-100 text-orange-700',
  'Alimentação':  'bg-yellow-100 text-yellow-700',
  'Manutenção':   'bg-red-100 text-red-700',
  'Material':     'bg-blue-100 text-blue-700',
  'Pedágio':      'bg-purple-100 text-purple-700',
  'Outros':       'bg-slate-100 text-slate-600',
};

const today = () => new Date().toISOString().slice(0, 10);

const emptyForm = {
  data: today(),
  categoria: 'Combustível',
  descricao: '',
  valor: '',
  forma_pagamento: 'Pix',
  viagem_id: '',
  responsavel: '',
  observacoes: '',
};

function fmtVal(v) {
  return v != null ? `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-';
}
function fmtDate(d) {
  if (!d) return '-';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

export default function Despesas() {
  const { user } = useAuth();
  const { showLoading, hideLoading } = useLoading();
  const canWrite = user?.role !== 'financeiro';

  const [despesas, setDespesas] = useState([]);
  const [viagens, setViagens] = useState([]);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const f = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (catFilter) params.categoria = catFilter;
      if (dataInicio) params.data_inicio = dataInicio;
      if (dataFim) params.data_fim = dataFim;
      const [{ data: desp }, { data: viag }] = await Promise.all([
        api.get('/despesas', { params }),
        api.get('/viagens', { params: { limit: 500 } }),
      ]);
      setDespesas(desp);
      setViagens(viag.viagens || viag || []);
    } catch {
      toast.error('Erro ao carregar despesas');
    } finally {
      setLoading(false);
    }
  }, [search, catFilter, dataInicio, dataFim]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNew = () => {
    setForm({ ...emptyForm, data: today(), responsavel: user?.nome || '' });
    setEditingId(null);
    setModalOpen(true);
  };

  const openEdit = (d) => {
    setForm({
      data: d.data || today(),
      categoria: d.categoria || 'Outros',
      descricao: d.descricao || '',
      valor: d.valor != null ? String(d.valor) : '',
      forma_pagamento: d.forma_pagamento || 'Pix',
      viagem_id: d.viagem_id || '',
      responsavel: d.responsavel || '',
      observacoes: d.observacoes || '',
    });
    setEditingId(d.id);
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.descricao.trim()) { toast.error('Informe a descrição'); return; }
    if (!form.valor || isNaN(Number(form.valor)) || Number(form.valor) <= 0) {
      toast.error('Informe um valor válido'); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        valor: Number(form.valor),
        viagem_id: form.viagem_id || null,
      };
      if (editingId) {
        await api.put(`/despesas/${editingId}`, payload);
        toast.success('Despesa atualizada');
      } else {
        await api.post('/despesas', payload);
        toast.success('Despesa lançada');
      }
      setModalOpen(false);
      fetchAll();
    } catch {
      toast.error('Erro ao salvar despesa');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    showLoading('Excluindo...');
    try {
      await api.delete(`/despesas/${deleteId}`);
      toast.success('Despesa excluída');
      setDeleteId(null);
      fetchAll();
    } catch {
      toast.error('Erro ao excluir');
    } finally {
      hideLoading();
    }
  };

  const exportXLSX = () => {
    const rows = despesas.map(d => ({
      'Data': fmtDate(d.data),
      'Categoria': d.categoria || '-',
      'Descrição': d.descricao || '-',
      'Viagem': viagens.find(v => v.id === d.viagem_id)?.titulo || '-',
      'Valor': Number(d.valor) || 0,
      'Forma Pagamento': d.forma_pagamento || '-',
      'Responsável': d.responsavel || '-',
      'Observações': d.observacoes || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [10, 14, 30, 28, 12, 16, 20, 30].map(w => ({ wch: w }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Despesas');
    XLSX.writeFile(wb, `despesas_${today()}.xlsx`);
  };

  const totalDespesas = despesas.reduce((acc, d) => acc + (Number(d.valor) || 0), 0);
  const getViagem = (id) => viagens.find(v => v.id === id);

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow" style={{ background: 'linear-gradient(135deg,#d72d21,#ff5a4a)' }}>
            <TrendingDown className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Despesas</h1>
            <p className="text-xs text-slate-500">Controle de saídas financeiras</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={exportXLSX} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
            <FileDown className="w-4 h-4" /> Excel
          </button>
          {canWrite && (
            <button onClick={openNew} className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-lg shadow transition-all" style={{ background: 'linear-gradient(135deg,#0147a0,#0163d2)' }}>
              <Plus className="w-4 h-4" /> Nova Despesa
            </button>
          )}
        </div>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm col-span-2 md:col-span-1">
          <p className="text-xs text-slate-500 mb-1">Total de Saídas</p>
          <p className="text-2xl font-bold text-red-600">{fmtVal(totalDespesas)}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
          <p className="text-xs text-slate-500 mb-1">Lançamentos</p>
          <p className="text-2xl font-bold text-slate-700">{despesas.length}</p>
        </div>
        {CATEGORIAS.slice(0, 2).map(cat => {
          const total = despesas.filter(d => d.categoria === cat).reduce((s, d) => s + (Number(d.valor) || 0), 0);
          return (
            <div key={cat} className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
              <p className="text-xs text-slate-500 mb-1">{cat}</p>
              <p className="text-xl font-bold text-slate-700">{fmtVal(total)}</p>
            </div>
          );
        })}
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 flex flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[180px] bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
          <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <input
            className="bg-transparent text-sm w-full outline-none text-slate-700 placeholder-slate-400"
            placeholder="Buscar descrição ou responsável..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select
          className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-slate-50 text-slate-700 outline-none"
          value={catFilter}
          onChange={e => setCatFilter(e.target.value)}
        >
          <option value="">Todas as categorias</option>
          {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input type="date" className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-slate-50 text-slate-700 outline-none" value={dataInicio} onChange={e => setDataInicio(e.target.value)} title="Data início" />
        <input type="date" className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-slate-50 text-slate-700 outline-none" value={dataFim} onChange={e => setDataFim(e.target.value)} title="Data fim" />
        {(catFilter || dataInicio || dataFim) && (
          <button onClick={() => { setCatFilter(''); setDataInicio(''); setDataFim(''); }} className="text-xs text-slate-400 hover:text-slate-600 px-2">Limpar</button>
        )}
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400">Carregando...</div>
        ) : despesas.length === 0 ? (
          <div className="p-12 text-center">
            <TrendingDown className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">Nenhuma despesa encontrada</p>
            <p className="text-slate-400 text-sm mt-1">{canWrite ? 'Clique em "Nova Despesa" para lançar a primeira saída.' : 'Nenhum lançamento neste período.'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Data</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Categoria</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Descrição</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Viagem</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Valor</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Forma</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Responsável</th>
                  {canWrite && <th className="px-4 py-3" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {despesas.map((d) => {
                  const viag = getViagem(d.viagem_id);
                  return (
                    <tr key={d.id} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(d.data)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${CAT_CLS[d.categoria] || 'bg-slate-100 text-slate-600'}`}>
                          {d.categoria}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-700 font-medium max-w-[200px] truncate">{d.descricao}</td>
                      <td className="px-4 py-3 hidden md:table-cell text-slate-500 text-xs max-w-[160px] truncate">
                        {viag ? viag.titulo : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-red-600 whitespace-nowrap">{fmtVal(d.valor)}</td>
                      <td className="px-4 py-3 hidden lg:table-cell text-slate-500">{d.forma_pagamento || '-'}</td>
                      <td className="px-4 py-3 hidden lg:table-cell text-slate-500">{d.responsavel || '-'}</td>
                      {canWrite && (
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 justify-end">
                            <button onClick={() => openEdit(d)} className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => setDeleteId(d.id)} className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-200 bg-slate-50">
                  <td colSpan={canWrite ? 4 : 3} className="px-4 py-3 text-sm font-semibold text-slate-600 hidden md:table-cell">Total ({despesas.length} lançamentos)</td>
                  <td colSpan={canWrite ? 4 : 3} className="px-4 py-3 text-sm font-semibold text-slate-600 md:hidden">Total</td>
                  <td className="px-4 py-3 text-right font-bold text-red-600 text-base whitespace-nowrap">{fmtVal(totalDespesas)}</td>
                  <td colSpan={canWrite ? 3 : 2} className="hidden lg:table-cell" />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Modal form */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <h2 className="font-bold text-slate-800 text-lg">{editingId ? 'Editar Despesa' : 'Nova Despesa'}</h2>
              <button onClick={() => setModalOpen(false)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Data *</label>
                  <input type="date" required className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400" value={form.data} onChange={e => f('data', e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Categoria *</label>
                  <select required className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400" value={form.categoria} onChange={e => f('categoria', e.target.value)}>
                    {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Descrição *</label>
                <input required className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400" placeholder="Ex: Abastecimento do ônibus" value={form.descricao} onChange={e => f('descricao', e.target.value)} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Valor (R$) *</label>
                  <input required type="number" step="0.01" min="0.01" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400" placeholder="0,00" value={form.valor} onChange={e => f('valor', e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Forma de Pagamento</label>
                  <select className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400" value={form.forma_pagamento} onChange={e => f('forma_pagamento', e.target.value)}>
                    {FORMAS_PAG.map(fp => <option key={fp} value={fp}>{fp}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Vincular a Viagem <span className="font-normal text-slate-400">(opcional)</span></label>
                <select className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400" value={form.viagem_id} onChange={e => f('viagem_id', e.target.value)}>
                  <option value="">— Despesa geral —</option>
                  {viagens.map(v => (
                    <option key={v.id} value={v.id}>{v.titulo} ({v.data ? v.data.split('-').reverse().join('/') : ''})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Responsável</label>
                <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400" placeholder="Nome de quem lançou" value={form.responsavel} onChange={e => f('responsavel', e.target.value)} />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Observações</label>
                <textarea rows={2} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 resize-none" placeholder="Detalhes adicionais..." value={form.observacoes || ''} onChange={e => f('observacoes', e.target.value)} />
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setModalOpen(false)} className="flex-1 py-2.5 px-4 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                  Cancelar
                </button>
                <button type="submit" disabled={saving} className="flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold text-white shadow transition-all disabled:opacity-60" style={{ background: 'linear-gradient(135deg,#0147a0,#0163d2)' }}>
                  {saving ? 'Salvando...' : editingId ? 'Salvar Alterações' : 'Lançar Despesa'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal confirmar delete */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="font-bold text-slate-800 mb-2">Excluir despesa?</h3>
            <p className="text-slate-500 text-sm mb-6">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">Cancelar</button>
              <button onClick={handleDelete} className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
